import {useEffect, useRef, useState} from 'react'
import {useLazyQuery, DocumentNode} from '@apollo/client'
import {GET_COMMISSION_REPORT_ASYNC_TASK} from '../gql/queries/commissionReportsApiQueries'
import {IAsyncTask} from '../types/commissionReportApi'

interface UseAsyncTaskPollingOptions {
  taskId: string | null
  /** Custom GQL query — defaults to GET_COMMISSION_REPORT_ASYNC_TASK */
  query?: DocumentNode
  /** Key in the query response that holds the task object — defaults to 'commissionReportAsyncTask' */
  dataKey?: string
  onSuccess?: (result: string) => void
  onError?: (error: string) => void
  pollingInterval?: number // milliseconds, default: 2000
}

interface UseAsyncTaskPollingResult {
  task: IAsyncTask | null
  isPolling: boolean
  stopPolling: () => void
}

export const useAsyncTaskPolling = ({
  taskId,
  query: customQuery,
  dataKey = 'commissionReportAsyncTask',
  onSuccess,
  onError,
  pollingInterval = 2000,
}: UseAsyncTaskPollingOptions): UseAsyncTaskPollingResult => {
  const [task, setTask] = useState<IAsyncTask | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const hasCompletedRef = useRef<boolean>(false)

  const [getTaskStatus, {data, loading, error}] = useLazyQuery(
    customQuery || GET_COMMISSION_REPORT_ASYNC_TASK,
    {
      fetchPolicy: 'network-only', // Always fetch fresh data
    }
  )

  const stopPolling = () => {
    console.log('[useAsyncTaskPolling] Stopping polling...')
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setIsPolling(false)
    setTask(null)
    hasCompletedRef.current = false
  }

  useEffect(() => {
    if (!taskId) {
      stopPolling()
      setTask(null)
      return
    }

    hasCompletedRef.current = false
    setIsPolling(true)

    // Immediate first fetch
    getTaskStatus({variables: {taskId}})

    // Start polling
    intervalRef.current = setInterval(() => {
      // Don't fetch if already completed
      if (hasCompletedRef.current) {
        console.log('[useAsyncTaskPolling] Task already completed, skipping fetch')
        return
      }
      getTaskStatus({variables: {taskId}})
    }, pollingInterval)

    // Cleanup on unmount or when taskId changes
    return () => {
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, pollingInterval])

  useEffect(() => {
    if (data?.[dataKey]) {
      const taskData = data[dataKey] as IAsyncTask

      // Debug logging (remove in production)
      console.log(
        '[useAsyncTaskPolling] Task status:',
        taskData.status,
        'TaskId:',
        taskData.taskId,
        'hasCompleted:',
        hasCompletedRef.current
      )

      // If already completed, ignore any further updates
      if (hasCompletedRef.current) {
        console.log('[useAsyncTaskPolling] Already completed, ignoring update')
        return
      }

      // Update task state ONLY if still polling (not already completed)
      if (
        taskData.status === 'PENDING' ||
        taskData.status === 'PROCESSING' ||
        taskData.status === 'PROGRESS'
      ) {
        setTask(taskData)
      }

      // Check if task is complete
      if (taskData.status === 'SUCCESS') {
        console.log('[useAsyncTaskPolling] Task completed successfully, stopping polling')
        // Mark as completed IMMEDIATELY to prevent further polling
        hasCompletedRef.current = true
        // Stop polling
        stopPolling()
        // Call callback
        if (onSuccess) {
          onSuccess(taskData.result || 'success')
        }
      } else if (taskData.status === 'FAILURE') {
        console.log('[useAsyncTaskPolling] Task failed, stopping polling')
        // Mark as completed IMMEDIATELY to prevent further polling
        hasCompletedRef.current = true
        // Stop polling
        stopPolling()
        // Call callback
        if (onError) {
          onError(taskData.errorMessage || 'Unknown error occurred')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (error) {
      stopPolling()
      if (onError) {
        onError(error.message)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  return {
    task,
    isPolling,
    stopPolling,
  }
}
